import React, { useContext, useCallback, useMemo } from "react";
import {
  Box,
  useTheme,
  Typography,
  Tab,
  Grid,
  FormControlLabel,
  Switch,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Drawer,
  Button,
  Skeleton,
} from "@mui/material";
import { SearchBar } from "./SearchBar";
import { ProductImagePopup } from "./Table/Popup/ProductImagePopup";
import {
  AdData,
  CountryMapData,
  GeographicalSummaryData,
  MapOfBrands,
  ProductData,
} from "~src/types/brandMapping";
import IconButton from "@mui/material/IconButton";
import Iconify from "~src/components/Iconify";
import { AdImagePopup } from "./Table/Popup/AdImagePopup";
import CoverCards from "../competitorAnalytics/CoverSection/CoverCards";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import ContentBox from "~src/components/ContentBox";
import GeoMap from "./GeoMap";
import EmptyContent from "./EmptyResponse";
import { MapOfBrandList } from "./Table/MapOfBrandList";
import FilterDrawer from "./FilterDrawer";
import { BrandMappingFilters } from "~src/hooks/useBrandMappingV3";
import FilterChips from "./FilterDrawer/FilterChips";
import { TableSkeleton } from "~src/components/table";
import { CountryType } from "~src/data/countries";
import { CategoryList } from "./Categorisation/CategoryList";
import { BrandMappingContextV3 } from "~src/contexts/BrandMappingContextV3";

type PropsType = {
  tabVersion: string;
  setBrandToRemove?: React.Dispatch<React.SetStateAction<MapOfBrands[] | undefined>>;
  exactMatch: boolean;
  setExactMatch: React.Dispatch<React.SetStateAction<boolean>>;
  searchText: string | undefined;
  setSearchText: React.Dispatch<React.SetStateAction<string | undefined>>;
  mappedBrands: MapOfBrands[] | undefined;
  filters?: BrandMappingFilters | undefined;
  onFiltersUpdated?: React.Dispatch<React.SetStateAction<BrandMappingFilters | undefined>>;
  brandCountries?: CountryType[];
  loadMore: () => void;
  geographicalSummary: GeographicalSummaryData | undefined;
  loading: boolean;
  initialLoading: boolean;
};

const mapTabsConfig = [
  {
    label: "Brand",
    value: "brand",
    tooltip:
      "Explore countries ranked by the total number of brands, highlighting their online presence. Markets are ranked as Established, indicating high brand saturation, or Opportunities, showcasing potential growth areas. Use the map to uncover key markets",
  },
  {
    label: "Traffic",
    value: "traffic",
    tooltip:
      "View the total website traffic for all brands in each country. Markets are ranked as Established, reflecting strong online presence, or Growing and Opportunities, showcasing areas with growth potential. Use the map to identify key traffic trends.",
  },
  {
    label: "Facebook Ads",
    value: "fb_ads",
    tooltip: "View the total website ads for all brands in each country.",
  },
  {
    label: "Shipping to Countries",
    value: "shipping_to_countries",
    tooltip:
      "Discover the countries where most brands ship their products. Markets are categorized as Established, indicating strong demand, or Growing and Opportunities, highlighting areas with potential for growth. Explore the map to identify key shipping destinations.",
  },
];

const viewTypeConfig = [
  { label: "List View", value: "list_view", icon: "bx:list-ul" },
  { label: "Map View", value: "map_view", icon: "material-symbols-light:map-outline" },
];

type TabValues = "brand" | "traffic" | "fb_ads" | "shipping_to_countries";

export default function BrandMapping({
                                       tabVersion,
                                       setBrandToRemove,
                                       exactMatch,
                                       setExactMatch,
                                       searchText,
                                       setSearchText,
                                       mappedBrands,
                                       filters,
                                       onFiltersUpdated,
                                       brandCountries,
                                       loadMore,
                                       geographicalSummary,
                                       loading,
                                       initialLoading,
                                     }: PropsType) {
  const theme = useTheme();
  const [productImagePopup, setProductImagePopup] = React.useState<boolean>(false);
  const [adImagePopup, setAdImagePopup] = React.useState<boolean>(false);
  const [selectedProuductImage, setSelectedProuductImage] = React.useState<ProductData>();
  const [selectedAdImage, setSelectedAdImage] = React.useState<AdData>();
  const [mapTab, setMapTab] = React.useState(mapTabsConfig[0].value);
  const [selectedBrand, setSelectedBrand] = React.useState<MapOfBrands>();
  const [drawer, setDrawer] = React.useState<boolean>(false);

  const mappedBrandContextV3 = useContext(BrandMappingContextV3);

  const maxFollowers =
    mappedBrands &&
    Math.max(
      ...mappedBrands.map(
        (brand) =>
          brand.brand.latest_fblikes + brand.brand.latest_instafollowers,
      ),
    );

  const onSearch = (searchString: string | undefined) => {
    setSearchText(searchString);
  };

  const filtersApplied = React.useMemo(() => {
    return Object.keys(filters || {}).length > 0;
  }, [filters]);

  const expandProductImage = (data: ProductData, brand: MapOfBrands) => {
    setSelectedProuductImage(data);
    setSelectedBrand(brand);
    setProductImagePopup(true);
  };

  const expandAdImage = (data: AdData, brand: MapOfBrands) => {
    setSelectedAdImage(data);
    setSelectedBrand(brand);
    setAdImagePopup(true);
  };

  const [clickedBrand, setClickedBrand] = React.useState<MapOfBrands | undefined>();

  const handleMapTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setMapTab(newValue);
  };

  // Wrap brandConfig in useCallback so that its reference is stable.
  const brandConfig = useCallback((brand: MapOfBrands) => {
    let minPrice = Infinity;
    let maxPrice = -Infinity;

    const productImageUrls: ProductData[] = [];
    const adImageUrls: AdData[] = [];

    for (const product of brand.products) {
      if (product.min_price != null && product.max_price != null) {
        minPrice = Math.min(minPrice, product.min_price);
        maxPrice = Math.max(maxPrice, product.max_price);
      } else if (product.price != null) {
        minPrice = Math.min(minPrice, product.price);
        maxPrice = Math.max(maxPrice, product.price);
      }
      if (product) {
        productImageUrls.push(product);
      }
    }

    for (const ad of brand.ads) {
      if (ad) {
        adImageUrls.push(ad);
      }
    }

    const priceRange = {
      min: minPrice === Infinity ? 0 : minPrice,
      max: maxPrice === -Infinity ? "-" : maxPrice,
    };
    return { productImageUrls, adImageUrls, priceRange };
  }, []);

  // Since brandConfig is now stable via useCallback, you can memoize if needed.
  const memoizedBrandConfig = useMemo(() => brandConfig, [brandConfig]);

  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setExactMatch(event.target.checked);
  };

  const [view, setView] = React.useState("list_view");

  const handleViewChange = (_event: React.MouseEvent<HTMLElement>, newView: string | null) => {
    if (newView !== null) {
      setView(newView);
    }
  };

  return (
    <Box sx={{ my: 2 }}>
      <Drawer anchor={"right"} open={drawer} onClose={() => setDrawer(false)}>
        <FilterDrawer
          filters={filters}
          brandCountries={brandCountries}
          closeDrawer={() => setDrawer(false)}
          onComplete={(filters) => {
            setDrawer(false);
            if (onFiltersUpdated) {
              onFiltersUpdated(filters);
            }
          }}
          onCancel={() => setDrawer(false)}
          maxFollowers={maxFollowers}
        />
      </Drawer>
      <ProductImagePopup
        open={productImagePopup}
        close={() => setProductImagePopup(false)}
        data={selectedProuductImage}
        selectedBrand={selectedBrand}
      />
      <AdImagePopup
        open={adImagePopup}
        close={() => setAdImagePopup(false)}
        data={selectedAdImage}
        selectedBrand={selectedBrand}
      />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 , sm: 6 }} >
          <SearchBar onSearch={onSearch} />
        </Grid>
        <Grid size={{ xs: 12 , sm: 2 }} >
          <Box
            sx={{
              width: { xs: "100%", md: 600 },
              mb: { xs: 2, md: 0 },
              display: "flex",
            }}
          >
            <FormControlLabel
              control={<Switch checked={exactMatch} onChange={handleToggle} />}
              label={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center"
                  }}>
                  Exact Match
                  <Tooltip
                    title={
                      <>
                        Show only results that match your search terms exactly.
                        Disable it to get broader search results.
                      </>
                    }
                    arrow
                  >
                    <IconButton sx={{ width: 30, height: 30 }}>
                      <Iconify icon={"iconoir:question-mark-circle"} />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
            />
          </Box>
        </Grid>
        <Grid size={{ xs: 12 , sm: 4 }}  sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "flex-end" }}>
            {mappedBrands && mappedBrands.length > 0 && (
              <ToggleButtonGroup
                color="primary"
                value={view}
                exclusive
                onChange={handleViewChange}
                aria-label="Platform"
              >
                {viewTypeConfig.map((item) => (
                  <ToggleButton key={item.value} value={item.value}>
                    <Iconify icon={item.icon} width={20} height={20} sx={{ mr: 1 }} />
                    {item.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
            {mappedBrands &&
              (mappedBrands.length > 0 || (filters && Object.keys(filters).length > 0)) &&
              (tabVersion === "v3" || tabVersion === "v2") && (
                <Button
                  variant={filtersApplied ? "contained" : "outlined"}
                  startIcon={<Iconify icon="ion:filter" />}
                  sx={{ borderRadius: "100px", height: "40px", alignSelf: "flex-end" }}
                  onClick={() => setDrawer(true)}
                >
                  Filters
                </Button>
              )}
          </Box>
        </Grid>
      </Grid>
      <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
        <FilterChips
          filters={filters}
          onFilterUpdate={onFiltersUpdated}
          maxFollowers={maxFollowers}
          brandCountries={brandCountries}
        />
      </Box>
      {tabVersion === "v3" && (
        <CategoryList
          categories={mappedBrandContextV3?.productCategories}
          filters={filters}
          onFiltersUpdated={onFiltersUpdated}
        />
      )}
      {filtersApplied && (
        <Button
          variant="outlined"
          startIcon={<Iconify icon="uil:times-circle" />}
          sx={{ borderRadius: "100px", height: "40px", ml: 2 }}
          onClick={() => {
            if (onFiltersUpdated) {
              onFiltersUpdated({});
            }
          }}
        >
          Clear all
        </Button>
      )}
      {!loading && (
        <Box>
          {view === "list_view" && (
            <Box>
              {!searchText && (!mappedBrands || mappedBrands.length === 0) ? (
                <CoverCards />
              ) : (
                mappedBrands &&
                mappedBrands.length > 0 && (
                  <MapOfBrandList
                    tabVersion={tabVersion}
                    setBrandToRemove={setBrandToRemove}
                    mappedBrands={mappedBrands}
                    brandConfig={memoizedBrandConfig}
                    clickedBrand={clickedBrand}
                    setClickedBrand={setClickedBrand}
                    expandAdImage={expandAdImage}
                    expandProductImage={expandProductImage}
                    setAdImagePopup={setAdImagePopup}
                    loading={loading}
                    loadMore={loadMore}
                  />
                )
              )}
            </Box>
          )}
          {view === "map_view" && (
            <ContentBox
              sx={{
                height: "100%",
                width: "100%",
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: "10px",
                mt: 2,
              }}
            >
              <Grid
                container
                spacing={2}
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between"
                }}>
                <Grid size={{ xs: 12, md: "auto" }}>
                  <Typography variant="h4">Map of Brands</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: "auto" }}>
                  <TabContext value={mapTab}>
                    <TabList onChange={handleMapTabChange} aria-label="lab API tabs example">
                      {mapTabsConfig.map((tabItem) => (
                        <Tab key={tabItem.value} label={tabItem.label} value={tabItem.value} />
                      ))}
                    </TabList>
                  </TabContext>
                </Grid>
              </Grid>
              <TabContext value={mapTab}>
                {mapTabsConfig.map((tabItem) => {
                  const trafficCountriesMap: Record<TabValues, CountryMapData[] | undefined> = {
                    brand: geographicalSummary?.top_brand_countries,
                    traffic: geographicalSummary?.top_traffic_countries,
                    fb_ads: geographicalSummary?.top_ad_countries,
                    shipping_to_countries: geographicalSummary?.top_shipping_countries,
                  };
                  const tabValue = tabItem.value as TabValues;
                  return (
                    <TabPanel key={tabItem.value} value={tabItem.value}>
                      <GeoMap loading={loading} trafficCountries={trafficCountriesMap[tabValue]} tabItem={tabItem} />
                    </TabPanel>
                  );
                })}
              </TabContext>
            </ContentBox>
          )}
        </Box>
      )}
      {loading && (
        <Box>
          {view === "list_view" ? (
            <Box
              sx={{
                backgroundColor: "background.default",
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: "12px",
                mt: 3,
                color: theme.palette.grey[200],
              }}
            >
              {Array.from({ length: 6 }).map((_, index) => (
                <TableSkeleton key={index} />
              ))}
            </Box>
          ) : (
            <Skeleton variant="rectangular" width="100%" height={500} sx={{ mt: 2, borderRadius: 1 }} />
          )}
        </Box>
      )}
      {searchText && mappedBrands && mappedBrands.length === 0 && !loading && !initialLoading && (
        <ContentBox sx={{ mt: 2 }}>
          <EmptyContent
            title="No Results Found"
            recommendationHeadline="Tips to improve your search:"
            recommendationPoints={[
              "Double-check your spelling",
              "Turn off Exact Match to Broaden your search results",
            ]}
          />
        </ContentBox>
      )}
    </Box>
  );
}
